import React, { useContext } from 'react';
import styled from 'styled-components';
import { SettingsContext } from '../contexts/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const CharacterList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const CharacterItem = styled.li`
  padding: var(--space-3) var(--space-4);
  background: ${props => props.isActive ?
    'rgba(74, 144, 226, 0.2)' :
    'rgba(255, 255, 255, 0.15)'
  };
  border-radius: var(--radius-xl);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  border: 2px solid ${props => props.isActive ?
    'rgba(74, 144, 226, 0.3)' :
    'rgba(255, 255, 255, 0.2)'
  };
  position: relative;
  font-family: var(--font-heading-medium);
  font-weight: 400;
  box-shadow: ${props => props.isActive ? '0 2px 8px rgba(74, 144, 226, 0.15)' : '0 1px 4px rgba(0, 0, 0, 0.05)'};
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  &:hover {
    background: ${props => props.isActive ? 'rgba(74, 144, 226, 0.25)' : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-2px) scale(1.01);
    box-shadow: ${props => props.isActive ? '0 4px 12px rgba(74, 144, 226, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
    border-color: ${props => props.isActive ? 'rgba(74, 144, 226, 0.4)' : 'rgba(255, 255, 255, 0.3)'};
  }

  &.active {
    background: rgba(74, 144, 226, 0.2);
    border-color: rgba(74, 144, 226, 0.3);
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.15);
    transform: translateY(-1px);
  }

  .dark-mode & {
    background: ${props => props.isActive ? 'rgba(187, 134, 252, 0.25)' : 'rgba(30, 30, 30, 0.3)'};
    border-color: ${props => props.isActive ? 'rgba(187, 134, 252, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
    &:hover {
      background: ${props => props.isActive ? 'rgba(187, 134, 252, 0.3)' : 'rgba(30, 30, 30, 0.4)'};
    }
    &.active {
      background: rgba(187, 134, 252, 0.25);
      border-color: rgba(187, 134, 252, 0.3);
    }
  }
`;

const ActiveDot = styled.span`
  width: 8px;
  height: 8px;
  background: var(--success-600);
  border-radius: var(--radius-full);
  margin-left: var(--space-3);
  display: ${props => props.active ? 'flex' : 'none'};
  flex-shrink: 0;
  box-shadow: 0 0 0 2px var(--surface-color);
  animation: ${props => props.active ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
      box-shadow: 0 0 0 2px var(--surface-color), 0 0 0 4px var(--success-600);
    }
    50% {
      transform: scale(1.1);
      opacity: 0.8;
      box-shadow: 0 0 0 2px var(--surface-color), 0 0 0 6px transparent;
    }
  }
`;

const AddCharacterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-3) var(--space-4);
  margin-top: var(--space-3);
  background: var(--surface-color);
  color: var(--text-secondary);
  border: 2px dashed var(--border-color);
  border-radius: var(--radius-xl);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: var(--font-heading-regular);
  font-weight: 300;
  font-size: var(--text-sm);
  position: relative;
  box-shadow: var(--shadow-sm);

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-600);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
    color: var(--primary-700);
  }

  &:active {
    transform: translateY(0);
    box-shadow: var(--shadow-sm);
  }

  .dark-mode & {
    background: var(--surface-color);
    &:hover {
      background: var(--primary-100);
    }
  }
`;

function Characters({ onCharacterEdit }) {
  const { characters, currentCharacter, setCurrentCharacter, setSettings, addCharacter } = useContext(SettingsContext);

  const handleCharacterClick = (character) => {
    setCurrentCharacter(character);
    setSettings({
      model: character.model,
      maxTokens: character.maxTokens,
      temperature: character.temperature,
      systemPrompt: character.systemPrompt,
    });
  };

  const handleCharacterDoubleClick = (character) => {
    if (onCharacterEdit) {
      onCharacterEdit(character);
    }
  };

  const handleAddCharacter = () => {
    const newCharacter = {
      name: `New Character ${characters.length + 1}`,
      description: '',
      model: 'claude-3-5-sonnet-20240620',
      maxTokens: 1000,
      temperature: 0.7,
      systemPrompt: '',
    };
    addCharacter(newCharacter);
    if (onCharacterEdit) {
      onCharacterEdit(newCharacter);
    }
  };

  return (
    <>
      <CharacterList>
        {characters.map((character) => (
          <CharacterItem 
            key={character.name} 
            onClick={() => handleCharacterClick(character)}
            onDoubleClick={() => handleCharacterDoubleClick(character)}
          >
            <ActiveDot active={currentCharacter && currentCharacter.name === character.name} />
            {character.name}
          </CharacterItem>
        ))}
      </CharacterList>
      <AddCharacterButton onClick={handleAddCharacter}>
        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
        Add New Character
      </AddCharacterButton>
    </>
  );
}

export default Characters;